import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useUsers } from "~/hooks";
import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { usePermissions } from "~/screens/Admins/hooks/usePermissions";
import { SOURCE, type ModalProps, type Source } from "~/shared.types";
import { Button } from "~/shared/components/ui/Button/Button";
import { Dropdown, icons, Modal, Toggle, type ModalAction } from "~/ui";
import { formatDateField } from "~/utils";
import { UserDetailRow, UserTypeButton, UserVisitsSection } from ".";
import { ConfirmEmailChangeModal } from "./components/ConfirmEmailChangeModal";
import { EditableEmailRow } from "./components/EditableEmailRow";
import { PatientRegistrationTable } from "./components/PatientRegistrationTable";
import { UserDetailsBanner } from "./components/UserDetailsBanner";
import { useEditableEmail } from "./hooks/useEditableEmail";
import { getPatientIdRows } from "./utils/patient-id";

const SOURCE_OPTIONS = Object.values(SOURCE).map((value) => ({
  label: value,
  value: value as Source,
}));

const EMPTY_VALUE = "-";

export const UserDetailsModal = ({ show, onClose }: ModalProps) => {
  const { canEditUserType, canEditSource, canMarkAsTest, canSendMagicLink } =
    usePermissions();

  const params = useParams<{ userId: string }>();
  const { getUser, updateUserFromAdminMutation, sendMagicLinkMutation } =
    useUsers();
  const navigateModal = useNavigateModal();
  const { data } = getUser(params.userId!);
  const { mutate: updateUserFromAdmin, isPending: isUpdating } =
    updateUserFromAdminMutation;
  const { mutate: sendMagicLink, isPending: isSendingMagicLink } =
    sendMagicLinkMutation;

  const user = data?.data;
  const {
    emailEdit,
    pendingEmail,
    isSavingEmail,
    confirmEmailChange,
    cancelEmailChange,
    closeEmailEdit,
  } = useEditableEmail(user);

  const handleTestUserToggle = () => {
    if (!user) return;

    updateUserFromAdmin({
      userId: params.userId!,
      isTest: !user.isTest,
    });
  };

  const handleSourceChange = (newSource: Source | null) => {
    if (!newSource || !user || newSource === user.source) return;

    updateUserFromAdmin({ userId: params.userId!, source: newSource });
  };

  const actions: ModalAction[] = [];

  if (canSendMagicLink && user) {
    actions.push({
      label: "Send Magic Link",
      disabled: isSendingMagicLink,
      onClick: () => sendMagicLink(user.id),
    });
  }

  const displayFields = useMemo(
    () => [
      {
        id: "joinedDate",
        field: "Hub registration date (ET)",
        value: formatDateField(user, "joinedDate", EMPTY_VALUE),
      },
      {
        id: "setPasswordDate",
        field: "Hub password set date (ET)",
        value: formatDateField(user, "setPasswordDate", EMPTY_VALUE),
      },
      {
        id: "lastLoginDate",
        field: "Last login date (ET)",
        value: formatDateField(user, "lastLoginDate", EMPTY_VALUE),
      },
      {
        id: "intakeSubmissionDate",
        field: "Intake form submission date",
        value: formatDateField(
          user?.intakeFormSubmission,
          "submissionDate",
          EMPTY_VALUE,
        ),
      },
      {
        id: "intakeSubmissionId",
        field: "Intake form submission ID",
        value: user?.intakeFormSubmission?.submissionId ?? EMPTY_VALUE,
        className: "pt-0",
      },
      {
        id: "lastPasswordResetDate",
        field: "Last password reset date (ET)",
        value: formatDateField(user, "lastPasswordUpdateDate", EMPTY_VALUE),
        className: "pt-0",
      },
    ],
    [user],
  );

  if (!user) return;

  return (
    <Modal
      className="gap-6"
      show={show}
      title={`${user.firstName} ${user.lastName}`}
      header={<UserDetailsBanner user={user} actions={actions} />}
      onClose={() => {
        closeEmailEdit();
        onClose();
      }}
    >
      <div className="gap-6">
        <div className="border-light-lavender-gray flex flex-col gap-4 border-b pb-4">
          <div className="flex items-start justify-between gap-4">
            <UserDetailRow
              field="Hub email"
              value={
                <EditableEmailRow email={user.email} emailEdit={emailEdit} />
              }
              className="min-w-0 flex-1"
            />
            {!emailEdit.isEditing && (
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0 gap-2"
                onClick={() =>
                  navigateModal(`${MODAL_ROUTES.staffBooking}/${params.userId}`)
                }
              >
                <icons.outline.CalendarIcon className="size-6" />
                Book a visit
              </Button>
            )}
          </div>

          <UserDetailRow
            field="Patient registration"
            value={<PatientRegistrationTable rows={getPatientIdRows(user)} />}
          />
        </div>
        <div className="border-light-lavender-gray flex flex-col gap-4 border-b py-4">
          <UserDetailRow
            field="User Type"
            value={
              <UserTypeButton
                userType={user.userType}
                onClick={() =>
                  navigateModal(MODAL_ROUTES.changeRole, {
                    user,
                  })
                }
                disabled={!canEditUserType}
              />
            }
          />

          <UserDetailRow
            field="Source"
            value={
              <Dropdown<Source>
                value={user.source}
                onChange={handleSourceChange}
                options={SOURCE_OPTIONS}
                disabled={isUpdating || !canEditSource}
                className="max-w-60"
              />
            }
          />

          <UserDetailRow
            field="Test User"
            value={
              <Toggle
                checked={Boolean(user.isTest)}
                onChange={handleTestUserToggle}
                disabled={isUpdating || !canMarkAsTest}
              />
            }
          />
        </div>
        <div className="border-light-lavender-gray flex flex-col gap-4 border-b py-4">
          <UserVisitsSection userId={params.userId!} />
        </div>
        <div className="flex flex-col gap-4 py-4">
          {displayFields.map((row) => (
            <UserDetailRow
              key={row.id}
              field={row.field}
              value={row.value}
              className={row.className}
            />
          ))}
        </div>
      </div>

      <ConfirmEmailChangeModal
        show={Boolean(pendingEmail)}
        firstName={user.firstName}
        currentEmail={user.email}
        newEmail={pendingEmail ?? ""}
        isSaving={isSavingEmail}
        onConfirm={confirmEmailChange}
        onClose={cancelEmailChange}
      />
    </Modal>
  );
};
